<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentPayment.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentPayment.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('studentPayment.criteria') }}</legend>
                  <div class="row">
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label class="required">{{
                          $t('yearlyPaymentList.branchName')
                        }}</label>
                        <a-select
                          style="width: 100%"
                          v-model.number="model.branch_id"
                          :options="branches"
                          @change="onBranchChange"
                        ></a-select>
                        <div class="validate" v-if="errors.has('branch_id')">
                          {{ errors.first('branch_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.acYearStatus') }}</label>
                        <a-select
                          style="width: 100%"
                          v-model.number="model.ac_year_status_id"
                          :options="academic_statuses"
                          @change="onAcademicYearStatusChange"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.acYear') }}</label>
                        <a-select
                          style="width: 100%"
                          v-model.number="model.ac_year_id"
                          :options="ac_year_list"
                          @change="onYearChange"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.mainProgram') }}</label>
                        <a-select
                          style="width: 100%"
                          mode="multiple"
                          :maxTagCount="1"
                          allowClear
                          show-search
                          :options="main_programs"
                          :placeholder="$t('all')"
                          v-model="model.acp_id"
                          @change="onMainProgramChange"
                          :disabled="!model.ac_year_id"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.subProgram') }}</label>
                        <a-select
                          style="width: 100%"
                          mode="multiple"
                          :maxTagCount="1"
                          allowClear
                          show-search
                          :options="sub_programs"
                          :placeholder="$t('all')"
                          v-model="model.acps_id"
                          @change="onSubChange"
                          :disabled="!model.ac_year_id"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.acTermStatus') }}</label>
                        <a-select
                          style="width: 100%"
                          v-model.number="model.ac_term_status_id"
                          :options="academic_statuses"
                          @change="onAcademicTermStatusChange"
                          :disabled="!model.ac_year_id"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.acTerm') }}</label>
                        <a-select
                          style="width: 100%"
                          mode="multiple"
                          :maxTagCount="1"
                          allowClear
                          show-search
                          :options="ac_terms"
                          :placeholder="$t('all')"
                          v-model="model.ac_term_id"
                          @change="onTermChange"
                          :disabled="!model.acps_id.length"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.classOperation') }}</label>
                        <a-select
                          style="width: 100%"
                          mode="multiple"
                          :maxTagCount="1"
                          allowClear
                          show-search
                          :options="class_options"
                          :placeholder="$t('all')"
                          v-model="model.class_id"
                        ></a-select>
                        <div class="validate" v-if="errors.has('class_id')">
                          {{ errors.first('class_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label :class="{ required: !hasSelectedClass }">{{
                          $t('studentPayment.dateRange')
                        }}</label>
                        <a-range-picker
                          style="width: 100%"
                          format="DD-MM-YYYY"
                          :defaultValue="rangeDate"
                          :defaultPickerValue="rangeDate"
                          @change="dateRangeChange"
                        >
                          <template slot="renderExtraFooter"></template>
                        </a-range-picker>
                        <div
                          class="validate"
                          v-if="
                            errors.has('date_range') ||
                              errors.has('date_from') ||
                              errors.has('date_to')
                          "
                        >
                          {{
                            errors.first('date_range') ||
                              errors.first('date_from') ||
                              errors.first('date_to')
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.studentStatus') }}</label>
                        <a-select
                          class="ts-w-full"
                          v-model="model.student_status_id"
                          :options="student_status"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.feeType') }}</label>
                        <a-select
                          style="width: 100%"
                          mode="multiple"
                          :maxTagCount="1"
                          allowClear
                          show-search
                          :options="fee_types"
                          :placeholder="$t('all')"
                          v-model="model.fee_type_id"
                        ></a-select>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group">
                        <label>{{
                          $t('studentEnrollmentReport.reportType')
                        }}</label>
                        <div class="d-flex align-items-center flex-wrap">
                          <div class="radio radio-css radio-inline m-r-15">
                            <input
                              v-model="model.report_type"
                              type="radio"
                              id="summary"
                              :value="'Summary'"
                            />
                            <label for="summary">
                              {{ $t('report.summary') }}
                            </label>
                          </div>
                          <div class="radio radio-css radio-inline">
                            <input
                              v-model="model.report_type"
                              type="radio"
                              id="detail"
                              :value="'Details'"
                            />
                            <label for="detail">
                              {{ $t('report.detail') }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6">
                      <div class="form-group report-action-group">
                        <label>&nbsp;</label>
                        <div class="row">
                          <div class="col-md-6">
                            <ts-button
                              color="success"
                              class="btn-block"
                              @click.prevent="exportExcel"
                              :waiting="exporting"
                            >
                              <i class="far fa-file-excel"></i>
                              {{ $t('export') }}
                            </ts-button>
                          </div>
                          <div class="col-md-6">
                            <ts-button
                              color="danger"
                              class="btn-block"
                              @click.prevent="preview"
                              :waiting="waiting"
                            >
                              <i class="far fa-file-pdf"></i>
                              {{ $t('preview') }}
                            </ts-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
    <div class="row m-t-10">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('studentPayment.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'studentPayment',
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      errors: new Errors(),
      class_options: [],
      ac_year_list: [],
      main_programs: [],
      sub_programs: [],
      ac_terms: [],
      fee_types: [],
      branches: [],
      src: '',
      academic_statuses: [
        { value: 1, label: this.$t('studentPayment.current') },
        { value: 2, label: this.$t('studentPayment.history') },
        { value: 3, label: this.$t('studentPayment.next') }
      ],
      student_status: [
        { value: 0, label: 'All' },
        { value: 1, label: 'Active' },
        { value: 2, label: 'Dropfed out' },
        { value: 3, label: 'Suspend' },
        { value: 4, label: 'Terminated' }
      ],
      rangeDate: [moment(), moment()],
      model: {
        student_status_id: 0,
        ac_year_status_id: 1,
        ac_term_status_id: 1,
        ac_year_id: null,
        acp_id: [],
        acps_id: [],
        ac_term_id: [],
        fee_type_id: [],
        branch_id: null,
        class_id: [],
        report_type: 'Summary'
      }
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    hasSelectedClass() {
      return this.model.class_id.length > 0
    }
  },
  methods: {
    ...mapActions('reportPayment', [
      'studentPayment',
      'studentPaymentExcel',
      'studentPaymentFormViewModel'
    ]),
    moment,
    async fetchData() {
      this.loading = true
      await this.fetchFormViewModel('branch,academicStatus,mainProgram,feeType')
        .then(response => {
          this.branches = response.branch
          this.academic_statuses = response.academicStatus
          this.main_programs = response.mainProgram
          this.fee_types = response.feeType
          this.model.branch_id = this.$store.state.authUser.default_branch_id
          this.onBranchChange()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    fetchFormViewModel(fnName, params = {}) {
      return this.studentPaymentFormViewModel(
        Object.assign(
          {
            fnName
          },
          params
        )
      )
    },
    onBranchChange() {
      this.clearYearCascade()
      this.loading = true
      this.fetchFormViewModel('academicYear', {
        ac_year_status_id: this.model.ac_year_status_id,
        branch_id: this.model.branch_id
      })
        .then(response => {
          this.ac_year_list = response.academicYear
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onAcademicYearStatusChange() {
      this.onBranchChange()
    },
    onYearChange() {
      this.clearProgramCascade()
    },
    onAcademicTermStatusChange() {
      this.clearTermCascade()
      this.onSubChange()
    },
    onMainProgramChange() {
      this.clearSubProgramCascade()
      if (!this.model.acp_id.length) {
        return
      }
      this.loading = true
      this.fetchFormViewModel('subProgram', {
        acp_id: this.model.acp_id
      })
        .then(response => {
          this.sub_programs = response.subProgram
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSubChange() {
      this.clearTermCascade()
      if (!this.model.acps_id.length) {
        return
      }
      this.loading = true
      this.fetchFormViewModel('academicTerm', {
        acps_id: this.model.acps_id,
        ac_term_status_id: this.model.ac_term_status_id,
        ac_year_id: this.model.ac_year_id,
        branch_id: this.model.branch_id
      })
        .then(response => {
          this.ac_terms = response.academicTerm
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onTermChange() {
      this.clearClassCascade()
      if (!this.model.ac_term_id.length) {
        return
      }
      this.loading = true
      this.fetchFormViewModel('classOperation', {
        branch_id: this.model.branch_id,
        ac_term_status_id: this.model.ac_term_status_id,
        ac_year_id: this.model.ac_year_id,
        acp_id: this.model.acp_id,
        acps_id: this.model.acps_id,
        ac_term_id: this.model.ac_term_id
      })
        .then(response => {
          this.class_options = response.classOperation
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearYearCascade() {
      this.ac_year_list = []
      this.model.ac_year_id = null
      this.clearProgramCascade()
    },
    clearProgramCascade() {
      this.model.acp_id = []
      this.clearSubProgramCascade()
    },
    clearSubProgramCascade() {
      this.sub_programs = []
      this.model.acps_id = []
      this.clearTermCascade()
    },
    clearTermCascade() {
      this.ac_terms = []
      this.model.ac_term_id = []
      this.clearClassCascade()
    },
    clearClassCascade() {
      this.class_options = []
      this.model.class_id = []
    },
    dateRangeChange(dateRange) {
      if (!dateRange || !dateRange.length) {
        this.$store.commit('reportPayment/SET_DATE_FROM', null)
        this.$store.commit('reportPayment/SET_DATE_TO', null)
        return
      }

      this.$store.commit(
        'reportPayment/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'reportPayment/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.studentPaymentExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'.indexOf('filename')]
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.studentPayment(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  }
}
</script>
